import { useAtom, useAtomValue } from "jotai"
import { useEffect, useState } from "react"
import { useModal } from "~/core/contexts/ModalContext"
import { useToast } from "~/core/contexts/ToastContext"
import { useAllBalance } from "~/core/hooks/useAllBalance"
import { SETTINGS } from "../constants"
import { icons } from "../assets/icons/icons"
import { WETH_CONTRACT_ADDRESS, ZERO_ADDRESS } from "~/core/constants"
import { TOKENS } from "~/core/tokens"
import { CurrencyPriceSlot } from "~/core/components/CurrencyPriceSlot"
import { parseEther } from "viem"
import { atomShowCustomBetAmount, atomUserInput, atomHardMode, atomSelectedTierIndex, atomIsIgamingDemo, atomDemoBalanceUsd } from "../states"
import { formatUsdFromEth } from "../utils/demoUsd"
import { atomGameState, atomIsGamePreparing, atomIsReplayMode } from "../states/game.atoms"
import { ContainerShape, HexagonShape, InnerShape, InnerShape2 } from "./Shape"
import { NumericalInput } from "~/core/components/NumericalInput"
import { ConfirmRaiseModal } from "../modals/confirm.raise.modal"
import { ConfirmHardModeModal } from "../modals/confirm.hard.mode.modal"
import { blinkoHardModeStorage } from "~/core/storages"
import { useSound } from "./SoundHandler"
import * as RadixSwitch from "@radix-ui/react-switch"
import { useAccount } from "wagmi"
import WalletSlot from "~/core/components/WalletSlot"

const tierColors = [
  {
    backgroundColor: '#523010', // Lighter
    borderColor: '#B56E01',
    borderColorUnselected: 'rgba(189, 119, 4, 0.5)',
    tabBackgroundSelected: '#523010',
    tabBackgroundUnselected: 'rgba(110, 90, 75, 0.4)',
  },
  {
    backgroundColor: '#432004', // Medium (default)
    borderColor: '#B56E01',
    borderColorUnselected: 'rgba(189, 119, 4, 0.5)',
    tabBackgroundSelected: '#432004',
    tabBackgroundUnselected: 'rgba(92, 74, 58, 0.4)',
  },
  {
    backgroundColor: '#401803', // Darker with more red tint
    borderColor: '#B56E01',
    borderColorUnselected: 'rgba(189, 119, 4, 0.5)',
    tabBackgroundSelected: '#401803',
    tabBackgroundUnselected: 'rgba(90, 50, 35, 0.4)',
  },
]

export const PlaceYourBet = () => {
  const { getBalance } = useAllBalance()
  const sound = useSound()
  const { showModal } = useModal()
  const toast = useToast()
  const { address } = useAccount()

  const [showCustomInput, setShowCustomInput] = useAtom(atomShowCustomBetAmount)
  const [userInput, setUserInput] = useAtom(atomUserInput)
  const [isHardMode, setIsHardMode] = useAtom(atomHardMode)
  const isIgamingDemo = useAtomValue(atomIsIgamingDemo)
  const demoBalance = useAtomValue(atomDemoBalanceUsd)
  
  const gameState = useAtomValue(atomGameState)
  const isGamePreparing = useAtomValue(atomIsGamePreparing)
  const isReplayMode = useAtomValue(atomIsReplayMode)
  const disabled = gameState !== 'waiting' || isGamePreparing || isReplayMode

  const [rawAmount, setRawAmount] = useState(userInput.tollAmount)
  const maxTollAmount = isHardMode ? SETTINGS.HARD_MODE_MAX_TOLL_AMOUNT : SETTINGS.MAX_TOLL_AMOUNT
  
  const selectedTierIndex = Math.max(SETTINGS.TOLL_OPTIONS.findIndex(t => t.options.some(o => o.value === parseFloat(userInput.tollAmount))), 0)
  const currentTierOptions = SETTINGS.TOLL_OPTIONS[selectedTierIndex].options

  useEffect(() => {
    setUserInput({
      ...userInput,
      tollAmount: rawAmount ?? '0'
    })
  }, [rawAmount])

  useEffect(() => {
    if (isReplayMode) {
      const isInOptions = SETTINGS.TOLL_OPTIONS.some(x => x.options.some(o => o.value.toString() === userInput.tollAmount))
      if (!isInOptions) {
        setRawAmount(userInput.tollAmount)
        setShowCustomInput(true)
      }
    }
  }, [isReplayMode, userInput])

  const onEditCustom = () => {
    if (disabled) return;
    setShowCustomInput(true)
    setRawAmount(userInput.tollAmount)
    sound.play('click');
  }

  const onCloseCustom = () => {
    if (disabled) return;
    setShowCustomInput(false)
    setUserInput(prev => ({
      ...prev,
      tollAmount: currentTierOptions[0].value.toString()
    }))
    sound.play('click');
  }

  const onCustomInput = (v: string) => {
    // NumericalInput value is shown in USD when in demo mode, but rawAmount is always ETH
    const ethValue = isIgamingDemo ? parseFloat(v || '0') / 1000 : parseFloat(v || '0')
    if (ethValue > maxTollAmount) {
      const maxLabel = isIgamingDemo
        ? formatUsdFromEth(maxTollAmount)
        : `${maxTollAmount} ${userInput.tollToken.symbol}`
      toast.error(`Max bet is ${maxLabel}`)
      setRawAmount(maxTollAmount.toString())
      return
    }
    setRawAmount(isIgamingDemo ? ethValue.toString() : v)
  }

  const onSelectTier = (tierIndex: number) => {
    if (disabled) return;
    onSelectTollAmount(SETTINGS.TOLL_OPTIONS[tierIndex].options[0].value)
    setShowCustomInput(false)
  }

  const onSelectTollAmount = (value: number) => {
    if (disabled) return;
    if (value > maxTollAmount) {
      return
    }

    if (value > 0.01 && value > parseFloat(userInput.tollAmount)) {
      showModal(hide => (
        <ConfirmRaiseModal
          currentBet={userInput.tollAmount} 
          newBet={value.toString()} 
          onConfirm={() => {
            setUserInput(prev => ({
              ...prev,
              tollAmount: value.toString()
            }))
          }} 
          onDismiss={hide} 
        />
      ), {
        hideOnEscapeKey: true,
      })
      return
    }

    setUserInput(prev => ({
      ...prev,
      tollAmount: value.toString()
    }))
    sound.play('click');
  }

  if (!address && !isReplayMode && !isIgamingDemo) {
    return (
      <WalletSlot>
        {({ connect }) => (
          <button className="flex flex-col w-full md:w-[330px]" onClick={connect}>
            <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
              <div className="flex justify-center items-center gap-3 px-8 py-3">
                <img src={icons.wallet} className='w-5 h-5' />
                <p className="text-base font-blinko text-white">Connect Wallet</p>
              </div>
            </InnerShape>
          </button>
        )}
      </WalletSlot>
    )
  }

  return (
    <div
      className="flex flex-col w-full md:w-[330px]"
      aria-disabled={disabled}
      style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
    >
      {/* Tier Tabs as Header */}
      <div className='flex items-center justify-center gap-1 relative px-4 md:px-0' style={{ marginBottom: '-1px' }}>
        {SETTINGS.TOLL_OPTIONS.map((tier, index) => {
          const isSelected = selectedTierIndex === index
          const tierTabColors = tierColors[index]
          return (
            <button
              key={tier.label}
              onPointerDown={() => onSelectTier(index)}
              className="rounded-t transition-all relative flex-1 md:flex-none overflow-hidden"
              style={{ 
                borderTop: `2px solid ${isSelected ? tierTabColors.borderColor : tierTabColors.borderColorUnselected}`,
                borderLeft: `2px solid ${isSelected ? tierTabColors.borderColor : tierTabColors.borderColorUnselected}`,
                borderRight: `2px solid ${isSelected ? tierTabColors.borderColor : tierTabColors.borderColorUnselected}`,
                borderBottom: isSelected ? 'none' : `2px solid ${tierTabColors.borderColorUnselected}`,
                paddingBottom: isSelected ? '0px' : '0',
                zIndex: isSelected ? 10 : 0,
                marginBottom: isSelected ? '-2px' : '0',
              }}
            >
              {/* Background layers matching ContainerShape */}
              <div 
                className="absolute inset-0" 
                style={{ 
                  backgroundColor: isSelected ? tierTabColors.tabBackgroundSelected : tierTabColors.tabBackgroundUnselected,
                  opacity: isSelected ? 0.8 : 1,
                }}
              />
              {isSelected && (
                <div 
                  className="absolute inset-0"
                  style={{ backdropFilter: 'blur(5px)' }}
                />
              )}
              <div className="relative px-4 py-1 md:py-2 md:px-6">
                <p className={`text-[11px] font-blinko uppercase ${isSelected ? 'text-white' : 'text-white/60'}`}>
                  {tier.label}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2} backgroundColor={tierColors.backgroundColor}>
        <div className="flex flex-col gap-2 p-4">
          {/* Bet Amount Options */}
          <div className='relative'>
            <div className="flex gap-1" style={{ opacity: showCustomInput ? 0 : 1 }}>
              <div className='flex-1'>
                <InnerShape2 cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                  <div className="flex gap-1 p-1">
                    {currentTierOptions.map(option => {
                      const isDisabled = option.value > maxTollAmount
                      const label = isIgamingDemo && option.label !== 'MAX'
                        ? formatUsdFromEth(option.value)
                        : option.label

                      return (
                        <button
                          key={option.value}
                          className='flex-1'
                          onPointerDown={() => !isDisabled && onSelectTollAmount(option.value)}
                          disabled={isDisabled}
                          style={{ opacity: isDisabled ? 0.4 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                          {option.value.toString() === userInput.tollAmount ? (
                            <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                              <div className="flex justify-center items-center w-full h-8">
                                <p className="text-xs font-blinko text-white">{label}</p>
                              </div>
                            </InnerShape>
                          ) : (
                            <div className="flex justify-center items-center w-full h-8">
                              <p className="text-xs font-blinko text-white">{label}</p>
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </InnerShape2>
              </div>
              <button onPointerDown={onEditCustom}>
                <HexagonShape cornerRadius={6} strokeWidth={1}>
                  <div className="flex justify-center items-center p-2">
                    <img src={icons.edit} className='w-6 h-6' />
                  </div>
                </HexagonShape>
              </button>
            </div>

            <div className='absolute inset-0' style={{ opacity: showCustomInput ? 1 : 0, pointerEvents: showCustomInput ? 'auto' : 'none' }}>
              <div className="flex gap-1">
                <div className='flex-1'>
                  <InnerShape2 cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                    <NumericalInput
                      disabled={disabled}
                      className="flex-1 w-full h-10 pl-3 pr-0 py-3 bg-transparent text-sm font-bold text-white"
                      value={isIgamingDemo ? (parseFloat(rawAmount || '0') * 1000).toString() : rawAmount}
                      onUserInput={onCustomInput}
                    />
                    <div className='absolute top-1/2 -translate-y-1/2 right-3 pointer-events-none'>
                      <p className="text-[10px] font-medium text-white/50">{isIgamingDemo ? '$' : userInput.tollToken.symbol}</p>
                    </div>
                  </InnerShape2>
                </div>
                <button onPointerDown={onCloseCustom}>
                  <HexagonShape cornerRadius={6} strokeWidth={1}>
                    <div className="flex justify-center items-center p-2">
                      <img src={icons.close} className='w-6 h-6' />
                    </div>
                  </HexagonShape>
                </button>
              </div>
            </div>
          </div>

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-1">
              <RadixSwitch.Root
                checked={isHardMode}
                tabIndex={-1}
                onCheckedChange={checked => {
                  if (disabled) return;
                  
                  if (checked) {
                    showModal(hide => (
                      <ConfirmHardModeModal
                        onDismiss={hide}
                        onConfirm={() => {
                          // Cap amount logic
                          const currentAmount = parseFloat(userInput.tollAmount)
                          if (currentAmount > SETTINGS.HARD_MODE_MAX_TOLL_AMOUNT) {
                            setUserInput(prev => ({
                              ...prev,
                              tollAmount: SETTINGS.HARD_MODE_MAX_TOLL_AMOUNT.toString()
                            }))
                            setRawAmount(SETTINGS.HARD_MODE_MAX_TOLL_AMOUNT.toString())
                          }

                          setIsHardMode(true)
                          blinkoHardModeStorage.set('true')
                          sound.play('click')
                        }}
                      />
                    ))
                  } else {
                    setIsHardMode(false)
                    blinkoHardModeStorage.set('false')
                    sound.play('click')
                  }
                }}
                onKeyDown={(e) => {
                  e.preventDefault();
                }}
                onKeyUp={(e) => {
                  e.preventDefault();
                }}
                aria-label="Toggle hard mode"
                className="relative h-5 w-9 cursor-pointer rounded-full bg-[#FFFFFF33] p-0.5 transition data-[state=checked]:bg-[#FDC700] focus-visible:outline-none"
              >
                <RadixSwitch.Thumb className="block h-4 w-4 transition-transform duration-200 data-[state=checked]:translate-x-[16px] data-[state=unchecked]:translate-x-0">
                  <img src={icons.paw} className="h-4 w-4" />
                </RadixSwitch.Thumb>
              </RadixSwitch.Root>
              <p className="text-[10px] font-semibold uppercase text-white/70">
                <span className={!isHardMode ? "text-[#FDC700]" : "text-[#FFFFFFCC]"}>Normal</span>
                <span className="text-[8px]">/</span>
                <span className={isHardMode ? "text-[#FDC700]" : "text-[#FFFFFFCC]"}>Hard</span>
              </p>
            </div>

            {isIgamingDemo ? (
              <p className="text-xs text-white/80">Bet {formatUsdFromEth(userInput.tollAmount)}</p>
            ) : (
              <CurrencyPriceSlot currency={WETH_CONTRACT_ADDRESS}>
                {({ formatBalanceWithPrice }) => (
                  <p className="text-xs text-white/80">Bet {formatBalanceWithPrice(parseEther(userInput.tollAmount), TOKENS[ZERO_ADDRESS].decimals)}</p>
                )}
              </CurrencyPriceSlot>
            )}
            <div className="flex items-center relative gap-1">
              <img src={isIgamingDemo ? icons.cashBanknote : icons.wallet} className='w-4 h-4 opacity-80' />
              {isIgamingDemo ? (
                <p className="text-xs text-white/80">${demoBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              ) : (
                <p className="text-xs text-white/80">{getBalance(ZERO_ADDRESS).formattedBalance} {userInput.tollToken.symbol}</p>
              )}
            </div>
          </div>
        </div>
      </ContainerShape>
    </div>
  )
}
